import { LoadingScreen } from '@aibees/shared/components';
import { classnames } from '@aibees/shared/utils';
import { Button } from '@mui/material';
import { Suspense, lazy, memo } from 'react';
import { ENRICHMENT_OPTIONS } from './config';
import { CREATE_DIALOG_TYPE, useCreateEnrichmentContext } from './context';
import { useEnrichOptions } from './options';
import * as Styled from './styles';
import * as SX from './sx';
import type { CreateNewEnrichmentProps as Props } from './types';

const EnrichmentAddFolderDialog = lazy(() =>
  import('./components/enrichment-add-folder-dialog').then((module) => ({
    default: module.EnrichmentAddFolderDialog,
  }))
);

const EnrichmentConfirmDialog = lazy(() =>
  import('./components/enrichment-confirm-dialog').then((module) => ({
    default: module.EnrichmentConfirmDialog,
  }))
);

const EnrichmentDeleteDialog = lazy(() =>
  import('./components/enrichment-delete-dialog').then((module) => ({
    default: module.EnrichmentDeleteDialog,
  }))
);

const EnrichmentDeleteFolderDialog = lazy(() =>
  import('./components/enrichment-delete-folder-dialog').then((module) => ({
    default: module.EnrichmentDeleteFolderDialog,
  }))
);

const EnrichmentGetStartedDialog = lazy(() =>
  import('./components/enrichment-get-started-dialog').then((module) => ({
    default: module.EnrichmentGetStartedDialog,
  }))
);

const EnrichmentEmailTypeDialog = lazy(() =>
  import('./components/enrichment-email-type-dialog').then((module) => ({
    default: module.EnrichmentEmailTypeDialog,
  }))
);

const EnrichmentPreviewDialog = lazy(() =>
  import('./components/enrichment-preview-dialog').then((module) => ({
    default: module.EnrichmentPreviewDialog,
  }))
);

const EnrichmentProcessDialog = lazy(() =>
  import('./components/enrichment-process-dialog').then((module) => ({
    default: module.EnrichmentProcessDialog,
  }))
);

const EnrichmentTemplateDialog = lazy(() =>
  import('./components/enrichment-template-dialog').then((module) => ({
    default: module.EnrichmentTemplateDialog,
  }))
);

const EnrichmentExitConfirmDialog = lazy(() =>
  import('./components/enrichment-exit-confirm-dialog').then((module) => ({
    default: module.EnrichmentExitConfirmDialog,
  }))
);

export const NewEnrichmentDialog = memo(
  ({
    className,
    hidden,
    creditRoute,
    extraOptions,
    title,
    route,
    buttonSx,
    icon,
    newEnrichmentButtonSize = 'small',
    enrichmentFolder,
    apiCreate,
    apiDelete,
    apiUpdate,
  }: Props) => {
    const {
      dialogType,
      previousDialogType,
      handleOpenDialog,
      handleCancelExit,
      handleCloseDialog,
    } = useCreateEnrichmentContext();

    const options = useEnrichOptions();

    return (
      <Styled.Wrapper
        className={classnames('CreateNewEnrichment', className)}
        sx={SX.root(hidden)}
      >
        <Button
          color="primary"
          variant="contained"
          size={newEnrichmentButtonSize}
          startIcon={icon}
          sx={buttonSx}
          onClick={() => handleOpenDialog(CREATE_DIALOG_TYPE.SELECT_TYPE)}
        >
          {title}
        </Button>
        <Suspense fallback={<LoadingScreen />}>
          <EnrichmentGetStartedDialog
            enrichmentOptions={ENRICHMENT_OPTIONS}
            enrichmentFolder={enrichmentFolder}
            open={dialogType === CREATE_DIALOG_TYPE.SELECT_TYPE}
            onClose={handleCloseDialog}
          />
        </Suspense>
        <Suspense fallback={<LoadingScreen />}>
          <EnrichmentEmailTypeDialog
            open={dialogType === CREATE_DIALOG_TYPE.SELECT_EMAILENRICHMENT_TYPE}
            onClose={handleCloseDialog}
          />
        </Suspense>
        <EnrichmentDeleteFolderDialog apiDelete={apiDelete} />

        <EnrichmentAddFolderDialog
          apiCreate={apiCreate}
          apiUpdate={apiUpdate}
        />
        {/* <Suspense fallback={<LoadingScreen />}>
          <EnrichmentTemplateDialog templateColumns={templateColumns} />
        </Suspense> */}
        <Suspense fallback={<LoadingScreen />}>
          <EnrichmentProcessDialog
            open={dialogType === CREATE_DIALOG_TYPE.PROCESSING_CSV}
            route={route}
            onClose={handleCloseDialog}
          />
        </Suspense>
        <Suspense fallback={<LoadingScreen />}>
          <EnrichmentPreviewDialog
            open={
              dialogType === CREATE_DIALOG_TYPE.PREVIEW_DATA ||
              (dialogType === CREATE_DIALOG_TYPE.EXIT_CONFIRMATION &&
                previousDialogType === CREATE_DIALOG_TYPE.PREVIEW_DATA)
            }
            extraOptions={extraOptions}
            onClose={handleCloseDialog}
          />
        </Suspense>
        <Suspense fallback={<LoadingScreen />}>
          <EnrichmentConfirmDialog
            open={
              dialogType === CREATE_DIALOG_TYPE.CONFIRM_ENRICH ||
              (dialogType === CREATE_DIALOG_TYPE.EXIT_CONFIRMATION &&
                previousDialogType === CREATE_DIALOG_TYPE.CONFIRM_ENRICH)
            }
            creditRoute={creditRoute}
            options={options}
            onClose={handleCloseDialog}
            extraOptions={extraOptions}
          />
        </Suspense>

        {/* <EnrichmentInsufficientCreditsDialog
        open={dialogType === CREATE_DIALOG_TYPE.INSUFFICIENT_CREDITS}
        onClose={handleCloseDialog}
      /> */}
        <Suspense fallback={<LoadingScreen />}>
          <EnrichmentExitConfirmDialog
            open={dialogType === CREATE_DIALOG_TYPE.EXIT_CONFIRMATION}
            onCancel={handleCancelExit}
            onConfirmExit={handleCloseDialog}
          />
        </Suspense>
      </Styled.Wrapper>
    );
  }
);

NewEnrichmentDialog.displayName = 'NewEnrichmentDialog';
